import clsx from 'clsx';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { useDidUpdate } from 'rooks';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { faClose } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';

export interface FileDropperProps {
  text: string;
  fileDropCallback: (file: File) => Promise<void>;
  clearFileCallback: () => void;
  id: string;
  testId?: string;
  addFileIndicator?: React.ReactNode;
  actionText?: string;
  isValid?: boolean;
  filename?: string;
  fileExtension?: string;
  extraClassNames?: string;
  initUploadCount?: number;
}

const InnerFileDropper = (props: FileDropperProps) => {
  const {
    text,
    fileDropCallback,
    clearFileCallback,
    extraClassNames,
    fileExtension,
    id,
    testId = 'fileDropper',
    filename,
    isValid = true,
    addFileIndicator,
    actionText,
    initUploadCount,
  } = props;
  const [hovering, setHovering] = useState(false);
  const [isProcessing, setProcessing] = useState(false);

  const { t } = useTranslation();

  const handleFileChange = (file: File) => {
    setProcessing(true);
    fileDropCallback(file).finally(() => setProcessing(false));
  };

  const [, drop] = useDrop({
    accept: [NativeTypes.FILE],
    hover() {
      if (!isProcessing) {
        setHovering(true);
      }
    },
    drop(item: { files: File[] }) {
      if (isProcessing) {
        return;
      }

      handleFileChange(item.files[0]);
      setHovering(false);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  const dropRef = useRef<HTMLDivElement>(null);
  drop(dropRef);

  const inputFileRef = useRef<HTMLInputElement>(null);

  useDidUpdate(() => {
    !filename && inputFileRef.current?.click();
  }, [initUploadCount]);

  const renderAddFileIndicatorWithText = () => (
    <div className="flexColumnContainer tw:items-center tw:pr-sq-10 tw:pl-sq-10">
      {addFileIndicator ? addFileIndicator : <span className="fa-3x">+</span>}
      <span className="tw:ml-sq-10 tw:text-left">{t(text)}</span>
    </div>
  );

  return (
    <div
      data-testid={testId}
      className={clsx(
        filename ? 'file-drop-zone-uploaded' : 'file-drop-zone',
        extraClassNames,
        {
          'file-drop-zone-over': hovering,
          'file-drop-zone-error': !filename && !isValid,
        },
        'p5',
      )}
      ref={dropRef}
      onClick={() => !filename && inputFileRef.current?.click()}>
      {!filename && (
        <input
          data-testid={`${testId}Input`}
          multiple={false}
          type="file"
          accept={fileExtension}
          style={{ display: 'none' }}
          ref={inputFileRef}
          onChange={(event) => (event.target?.files?.[0] ? handleFileChange(event.target.files[0]) : _.noop)}
        />
      )}
      <div
        className={clsx(
          'flexColumnContainer',
          'tw:justify-between',
          'tw:items-center',
          filename ? 'tw:min-h-sq-75!' : 'tw:min-h-sq-60!',
        )}>
        {isProcessing && (
          <div className="flexColumnContainer tw:items-center tw:pl-sq-10">
            <IconWithSpinner spinning={true} extraClassNames="fa-lg" />
            <span className="tw:ml-sq-10">{t('LOADING')}</span>
          </div>
        )}
        {filename && !isProcessing && (
          <>
            <div className="flexRowContainer tw:items-start tw:pl-sq-5">
              <span>{t(actionText ? actionText : 'FILE_IMPORTED_AS')}</span>
              <span className="aggressiveWordBreak">{filename}</span>
            </div>
            <Icon
              icon={faClose}
              extraClassNames="cursorPointer pr5"
              testId={`${id}-remove`}
              onClick={clearFileCallback}
            />
          </>
        )}
        {!filename && !isProcessing && renderAddFileIndicatorWithText()}
      </div>
    </div>
  );
};

const FileDropperUnwrapped = (props: FileDropperProps) => (
  <DndProvider backend={HTML5Backend} context={window}>
    <InnerFileDropper {...props} />
  </DndProvider>
);

/**
 * Drag-and-drop element to receive file upload
 */
export const FileDropper = React.memo(FileDropperUnwrapped, (prev, next) => _.isEqual(prev, next));
